import { useRef, type KeyboardEvent } from "react";
import { Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { Translations } from "@/lib/i18n/types";
import { PROTOCOL_LAYOUTS, type ProtocolLayout } from "@/domains/pm/utils/protocolLayout";
import { selectionClasses } from "./styles";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

const ARROW_STEPS: Partial<Record<string, number>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/*
 * Each thumbnail is a sketch of the first page in that layout, drawn in the
 * PDF's own colours: it depicts paper, so it stays white in dark mode too.
 */
const INK = "#1a1c21";
const MUTED = "#9aa0a8";
const SURFACE = "#eef0f2";
const RULE = "#d1d4d9";
const RED = "#ba142b";
const NAVY = "#172133";

function TableRows({
  top,
  count,
  height,
  style,
}: {
  top: number;
  count: number;
  height: number;
  style: "boxed" | "striped" | "ruled";
}) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => {
        const y = top + index * height;
        return (
          <g key={index}>
            {style === "boxed" && (
              <rect
                x={5}
                y={y}
                width={50}
                height={height}
                fill="none"
                stroke={RULE}
                strokeWidth={0.5}
              />
            )}
            {style === "striped" && index % 2 === 1 && (
              <rect x={5} y={y} width={50} height={height} fill={SURFACE} />
            )}
            {style === "ruled" && (
              <line
                x1={5}
                x2={55}
                y1={y + height}
                y2={y + height}
                stroke={RULE}
                strokeWidth={0.4}
              />
            )}
            <rect x={8} y={y + height / 2 - 0.6} width={18} height={1.2} fill={MUTED} />
            <rect x={32} y={y + height / 2 - 0.6} width={9} height={1.2} fill={MUTED} />
          </g>
        );
      })}
    </>
  );
}

function ClassicSketch() {
  return (
    <>
      <rect x={5} y={5} width={5} height={5} fill={RED} />
      <line x1={5} x2={55} y1={13} y2={13} stroke={RULE} strokeWidth={0.5} />
      <rect x={5} y={17} width={22} height={3.5} fill={INK} />
      <rect x={5} y={24} width={50} height={11} fill={SURFACE} />
      {[0, 1, 2].map((index) => (
        <rect key={index} x={5 + index * 17} y={39} width={15.5} height={6} fill={SURFACE} />
      ))}
      <rect x={5} y={49} width={50} height={4} fill="#e5e7ea" />
      <TableRows top={53} count={5} height={5} style="boxed" />
    </>
  );
}

function ModernSketch() {
  const stripes = ["#2663eb", "#179959", "#8c9199"];
  return (
    <>
      <rect x={0} y={0} width={60} height={17} fill={NAVY} />
      <rect x={0} y={17} width={60} height={1} fill={RED} />
      <rect x={5} y={3.5} width={4.5} height={4.5} fill="#ffffff" />
      <rect x={5} y={11} width={24} height={3.5} fill="#ffffff" />
      {[0, 1].map((column) =>
        [0, 1].map((row) => (
          <g key={`${column}-${row}`}>
            <rect x={5 + column * 25} y={22 + row * 6} width={8} height={1} fill={MUTED} />
            <rect x={5 + column * 25} y={24.2 + row * 6} width={16} height={1.5} fill={INK} />
          </g>
        ))
      )}
      {stripes.map((color, index) => (
        <g key={color}>
          <rect
            x={5 + index * 17}
            y={37}
            width={15.5}
            height={8}
            fill="#ffffff"
            stroke={RULE}
            strokeWidth={0.4}
          />
          <rect x={5 + index * 17} y={37} width={1.2} height={8} fill={color} />
        </g>
      ))}
      <rect x={5} y={49} width={50} height={4.5} fill={NAVY} />
      <TableRows top={53.5} count={5} height={5.5} style="striped" />
    </>
  );
}

function CompactSketch() {
  return (
    <>
      <rect x={5} y={5} width={3.5} height={3.5} fill={RED} />
      <rect x={10.5} y={5.5} width={16} height={2.5} fill={INK} />
      <line x1={5} x2={55} y1={11} y2={11} stroke={RULE} strokeWidth={0.5} />
      {[0, 1, 2].map((row) =>
        [0, 1].map((column) => (
          <rect
            key={`${row}-${column}`}
            x={5 + column * 25}
            y={14 + row * 3}
            width={18}
            height={1}
            fill={MUTED}
          />
        ))
      )}
      <rect x={5} y={25} width={34} height={1.2} fill={INK} />
      <rect x={5} y={30} width={50} height={3} fill="#e5e7ea" />
      <TableRows top={33} count={12} height={3.6} style="ruled" />
    </>
  );
}

function LayoutSketch({ layout }: { layout: ProtocolLayout }) {
  return (
    <svg
      viewBox="0 0 60 84"
      aria-hidden
      className="h-28 w-auto rounded-sm border border-border bg-white shadow-sm"
    >
      {layout === "classic" && <ClassicSketch />}
      {layout === "modern" && <ModernSketch />}
      {layout === "compact" && <CompactSketch />}
    </svg>
  );
}

/**
 * The Design button on step 3: a choice of how the protocol looks, never of
 * what it says. Three cards, each with a sketch of a first page, chosen like a
 * radio group — one tab stop, arrows move the choice.
 */
export function ProtocolDesignPicker({
  value,
  copy,
  onChange,
}: {
  value: ProtocolLayout;
  copy: ProtocolCopy;
  onChange: (layout: ProtocolLayout) => void;
}) {
  const design = copy.design;
  const cards = useRef<Array<HTMLButtonElement | null>>([]);

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const step = ARROW_STEPS[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + PROTOCOL_LAYOUTS.length) % PROTOCOL_LAYOUTS.length;
    onChange(PROTOCOL_LAYOUTS[next]);
    cards.current[next]?.focus();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          // The icon alone on screen; its name, and the design in use, for
          // screen readers and as a tooltip.
          aria-label={`${design.button}: ${design.layouts[value].name}`}
          title={`${design.button}: ${design.layouts[value].name}`}
        >
          <Palette aria-hidden className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      {/* Portalled (the default): the dialog's transformed, overflow-clipped
          content would otherwise clip the panel. It has nothing to scroll. */}
      <PopoverContent align="end" className="w-[min(calc(100vw-2rem),36rem)]">
        <p className="text-sm font-semibold">{design.title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{design.description}</p>
        <div
          role="radiogroup"
          aria-label={design.title}
          className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3"
        >
          {PROTOCOL_LAYOUTS.map((layout, index) => {
            const selected = layout === value;
            return (
              <button
                key={layout}
                ref={(element) => {
                  cards.current[index] = element;
                }}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={selected ? 0 : -1}
                onKeyDown={(event) => onKeyDown(event, index)}
                onClick={() => onChange(layout)}
                className={cn(selectionClasses(selected), "flex-row sm:flex-col sm:items-center")}
              >
                <LayoutSketch layout={layout} />
                <span className="min-w-0 sm:text-center">
                  <span className="block text-sm font-medium">{design.layouts[layout].name}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                    {design.layouts[layout].description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
